import React, { createContext, useEffect, useMemo, useCallback } from 'react'
import { useAppDispatch } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import useReduxStateListener from 'hooks/useReduxStateListener'
import { accountAccessShowLogin } from 'actions/UiActions'
import useSocket, { SocketType } from '../hooks/useSocket'

type LoginListenerState = {
  loggedIn: boolean,
  customerId?: string,
  firstName?: string,
  lastName?: string,
  phone?: string
  email?: string
}

export const SocketContext = createContext<{
  socket: SocketType | undefined,
  onLogin:() => Promise<{
      loggedIn: boolean;
      customerId?: string;
      firstName?: string;
      lastName?: string;
      phone?:string;
      email?:string;
  }>
    }>({
      socket: undefined,
      onLogin: () => new Promise(() => {}),
    })

function SocketContextProvider({ children }) {
  const dispatch = useAppDispatch()

  const { socket, resetSocket } = useSocket()

  const loginListener = useReduxStateListener<LoginListenerState>((state) => {
    if (selectLoggedIn(state)) {
      return {
        loggedIn: true,
        customerId: state.auth.account.memberId,
        firstName: state.auth.account.givenName,
        lastName: state.auth.account.surname,
        phone: state.auth.account.email,
        email: state.auth.account.email,
      }
    } else if (!state.ui.modal.open) {
      return { loggedIn: false }
    }
  })

  const onLogin = useCallback(async() => {
    dispatch(accountAccessShowLogin())
    const { loggedIn, customerId, firstName, lastName, phone, email } = await loginListener.awaitResult()

    if (loggedIn) {
      await resetSocket()
    }

    return {
      loggedIn,
      customerId,
      firstName,
      lastName,
      phone,
      email,
    }
  }, [dispatch, loginListener, resetSocket])

  useEffect(() => {
    if (socket) {
      socket.connect()
    }
  }, [socket])

  const contextValue = useMemo(() => {
    return {
      socket,
      onLogin,
    }
  }, [socket, onLogin])

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  )
}

export default SocketContextProvider
